import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { Link, useNavigate } from "react-router-dom";
import { Button, InlineNotification, Loading } from "@carbon/react";
import Form from "../carbon/Form/Form";
import TextField from "../carbon/Form/TextField";
import SelectField, { SelectFieldOption } from "../carbon/Form/SelectField";
import PhoneField from "../carbon/Form/PhoneField";
import PageHeader from "../carbon/PageHeader/PageHeader";
import ConfirmDialog from "../carbon/ConfirmDialog/ConfirmDialog";
import RolePicker from "../carbon/RolePicker/RolePicker";
import {
  newUserValidationSchema,
  NewUserFormValues,
} from "./newUserValidationSchema";
import {
  ABILITIES,
  USER_COMPANY_LIST_FOR_BUYER_ADMIN,
  USER_COMPANY_LIST_FOR_BUYER_MANAGER,
  USER_COMPANY_LIST_FOR_BUYER,
  USER_COMPANY_LIST_FOR_SELLER_ADMIN,
  USER_COMPANY_LIST_FOR_SELLER_MANAGER,
  USER_COMPANY_LIST_FOR_SELLER,
  USER_COMPANY_TYPES,
  USER_TYPES,
} from "../../utils";
import {
  useAssignUserRoles,
  useCreateNewUser,
  useUsersByCompany,
} from "../../hooks/useUsers";
import { useUser, useNotification } from "../../context";
import "./newUserForm.scss";

// Which roles a caller may hand out depends on both which side of the marketplace they're on
// (buyer/seller) and their own userCompanyType — an Admin can create Managers, a Manager cannot,
// and so on. Carried over verbatim from the pre-Carbon form's JSX conditionals, just as a lookup
// table instead of six near-identical `{isBuy && user?.userCompanyType === X && (...)}` blocks.
const BUYER_ROLE_OPTIONS: Record<
  string,
  typeof USER_COMPANY_LIST_FOR_BUYER_ADMIN
> = {
  [USER_COMPANY_TYPES.ADMIN]: USER_COMPANY_LIST_FOR_BUYER_ADMIN,
  [USER_COMPANY_TYPES.MANAGER]: USER_COMPANY_LIST_FOR_BUYER_MANAGER,
  [USER_COMPANY_TYPES.BUYER]: USER_COMPANY_LIST_FOR_BUYER,
};

const SELLER_ROLE_OPTIONS: Record<
  string,
  typeof USER_COMPANY_LIST_FOR_BUYER_ADMIN
> = {
  [USER_COMPANY_TYPES.ADMIN]: USER_COMPANY_LIST_FOR_SELLER_ADMIN,
  [USER_COMPANY_TYPES.MANAGER]: USER_COMPANY_LIST_FOR_SELLER_MANAGER,
  [USER_COMPANY_TYPES.COMMERCIAL]: USER_COMPANY_LIST_FOR_SELLER,
};

const defaultValues: NewUserFormValues = {
  firstName: "",
  lastName: "",
  email: "",
  confirmEmail: "",
  phone: "",
  userCompanyType: "",
  parent: "",
};

/**
 * The i18n key explaining why `POST /user/new` failed. The backend has no error codes, only its
 * message, so the two known refusals are recognised by it: a taken email, and a caller without the
 * Tier-2 ability asking for a management role. Anything else gets a generic message rather than
 * being mislabelled as one of those.
 */
const createErrorKeyFor = (error: unknown): string => {
  const message: string =
    (error as any)?.response?.data?.message ?? "";
  if (/email already exist/i.test(message)) return "users.emailExists";
  if (/can't assign a management role/i.test(message))
    return "users.cannotAssignManagementRole";
  return "users.createError";
};

/**
 * The create-user form (`POST /user/new`), rebuilt on the Carbon form primitives (Task 7) in
 * place of 321 lines of Tailwind and hand-rolled RHF `register` wiring. Validation is
 * `newUserValidationSchema` (its own module now — see that file's docstring); this component
 * owns layout, the role/responsible option lists, and the confirm-then-submit flow.
 *
 * Unlike the pre-Carbon version, the "Add" button submits the form (running Yup) directly — the
 * old flow opened its "are you sure?" modal unconditionally on click and only ran validation
 * once the modal's own confirm button was pressed, which meant an empty form's field errors were
 * hidden behind a confirmation dialog until it closed. Here `pendingValues` only gets set once
 * `Form`'s `handleSubmit` has already validated, so an invalid submission shows its field errors
 * immediately and never opens the confirmation dialog at all.
 */
const NewUserForm = () => {
  const { user, isBuy, isSeller, can } = useUser();
  const canAssignRoles =
    can(ABILITIES.MANAGE_USERS) || can(ABILITIES.SELLER_MANAGE_USERS);
  const { openNotification } = useNotification();
  const { t } = useTranslation();
  const navigate = useNavigate();

  // The values a validated submit produced, held here only until the user confirms (or cancels)
  // — the same "which confirmation is open" shape User.tsx's `activationAction` uses, just
  // carrying the payload the confirmation is about instead of a fixed set of string labels.
  const [pendingValues, setPendingValues] = useState<NewUserFormValues | null>(
    null
  );

  // RBAC roles chosen for the new user — assigned in a second call once the user exists (the
  // create endpoint has no roles field). Additive to the legacy `userCompanyType` select.
  const [selectedRoleIds, setSelectedRoleIds] = useState<string[]>([]);

  const { control, handleSubmit } = useForm<NewUserFormValues>({
    resolver: yupResolver(newUserValidationSchema),
    defaultValues,
  });

  const { data: usersByCompany, isLoading: isLoadingUsersByCompany } =
    useUsersByCompany(user?.id ?? "");

  // Success/error UX is handled in `handleConfirm` (create-then-assign has to be sequenced, and
  // one toast + navigation must fire only after BOTH calls), so the hook-level callbacks are
  // deliberately no-ops. A failed create sets `createErrorKey`, which drives the inline notification.
  const [createErrorKey, setCreateErrorKey] = useState<string | null>(null);
  const { mutateAsync: createNewUserAsync, isPending: isCreatingUser } =
    useCreateNewUser(
    () => undefined,
    () => undefined
  );
  const { mutateAsync: assignUserRolesAsync, isPending: isAssigningRoles } =
    useAssignUserRoles();

  const roleOptions: SelectFieldOption[] = useMemo(() => {
    const table = isBuy
      ? BUYER_ROLE_OPTIONS
      : isSeller
        ? SELLER_ROLE_OPTIONS
        : {};
    const list = user?.userCompanyType
      ? table[user.userCompanyType]
      : undefined;

    return (list ?? []).map((option) => ({
      value: option.type,
      label: option.name,
    }));
  }, [isBuy, isSeller, user?.userCompanyType]);

  const parentOptions: SelectFieldOption[] = useMemo(() => {
    const colleagues = (usersByCompany ?? []).map((colleague) => ({
      value: colleague.id,
      label: colleague.fullName,
    }));
    // The pre-Carbon form always appended the caller themself as the last option;
    // `useUsersByCompany`'s own `select` already filters the caller out of `usersByCompany`, so
    // this isn't a duplicate entry.
    return user
      ? [...colleagues, { value: user.id, label: user.fullName }]
      : colleagues;
  }, [usersByCompany, user]);

  const handleConfirm = async () => {
    if (!pendingValues) return;
    try {
      const created = await createNewUserAsync({
        firstName: pendingValues.firstName,
        lastName: pendingValues.lastName,
        email: pendingValues.email,
        confirmEmail: pendingValues.confirmEmail,
        phone: pendingValues.phone,
        userCompanyType: pendingValues.userCompanyType,
        // The backend resolves this through `normalizeParent`, which expects a nested `{ id }`,
        // not a bare id string — see this task's brief. The pre-Carbon NewUserForm already sent
        // this shape correctly (unlike EditUserForm's no-parentId branch, which sends a bare
        // string and gets a 400 for it — a separate, pre-existing issue). `|| user?.id` (not the
        // original's `??`) so leaving "Responsible" untouched — its blank placeholder option —
        // falls back to the caller's own id instead of submitting an empty string as the id.
        parent: { id: pendingValues.parent || user?.id },
        role: USER_TYPES.USER,
        type: isBuy ? USER_TYPES.BUYER : USER_TYPES.SELLER,
        active: true,
        // Required by the backend on create but never used by this flow — accounts are activated
        // through a normal password reset, not this throwaway value. Carried over unchanged.
        password: Math.random().toString(36).slice(-8),
      });
      // Only reached when the create succeeded — a rejection (e.g. 400 duplicate email) jumps to
      // the catch below, so roles are never assigned to a user that doesn't exist.
      // The user now exists, so an assign failure must NOT fall into the create-failed catch
      // below (that would keep the form open and falsely imply nothing was created — a retry
      // would then hit duplicate-email). Swallow it, warn, and leave the form regardless.
      let rolesFailed = false;
      if (canAssignRoles && selectedRoleIds.length > 0) {
        try {
          await assignUserRolesAsync({
            id: created.id,
            roleIds: selectedRoleIds,
          });
        } catch {
          rolesFailed = true;
        }
      }
      setPendingValues(null);
      openNotification(
        rolesFailed ? t("users.createdRolesFailed") : t("users.success")
      );
      navigate("/users");
    } catch (error) {
      setPendingValues(null);
      setCreateErrorKey(createErrorKeyFor(error));
    }
  };

  return (
    <div>
      <PageHeader title={t("users.add")} />

      {createErrorKey ? (
        <InlineNotification
          kind="error"
          lowContrast
          hideCloseButton
          title={t(createErrorKey)}
          className="new-user-form__notification"
        />
      ) : null}

      {isLoadingUsersByCompany ? (
        <Loading withOverlay={false} description={t("cta.loading")} small />
      ) : (
        <Form
          handleSubmit={handleSubmit}
          onSubmit={(data) => setPendingValues(data)}
          noValidate
          className="new-user-form cds-form-surface"
        >
          <div className="new-user-form__grid">
            <TextField
              name="firstName"
              label={t("users.firstName")}
              control={control}
              required
            />
            <TextField
              name="lastName"
              label={t("users.lastName")}
              control={control}
              required
            />
            <TextField
              name="email"
              type="email"
              label={t("users.email")}
              control={control}
              required
            />
            <TextField
              name="confirmEmail"
              type="email"
              label={t("users.confirmEmail")}
              control={control}
              required
            />
            <PhoneField
              name="phone"
              label={t("users.phone")}
              countryCodeLabel={t("users.phoneCountryCode")}
              control={control}
              required
            />
            <SelectField
              name="userCompanyType"
              label={t("users.role")}
              control={control}
              options={roleOptions}
              placeholder={t("users.selectRole")}
            />
            {canAssignRoles ? (
              <RolePicker
                selectedIds={selectedRoleIds}
                onChange={setSelectedRoleIds}
                labelText={t("users.roles")}
              />
            ) : null}
            <SelectField
              name="parent"
              label={t("users.responsible")}
              control={control}
              options={parentOptions}
              placeholder={t("users.parentSelect")}
              optional
            />
          </div>

          <div className="new-user-form__actions">
            <Button as={Link} to="/users" kind="secondary">
              {t("cta.cancel")}
            </Button>
            <Button type="submit">{t("cta.add")}</Button>
          </div>
        </Form>
      )}

      <ConfirmDialog
        open={pendingValues !== null}
        title={t("users.add")}
        body={t("users.addQuestion")}
        confirmLabel={t("cta.add")}
        onConfirm={handleConfirm}
        onCancel={() => setPendingValues(null)}
        isPending={isCreatingUser || isAssigningRoles}
      />
    </div>
  );
};

export default NewUserForm;
